import classnames from 'classnames'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { requestGetEnrollmentsExist } from '../../../../actions/enrollmentActions'
import { useDispatch, useSelector } from 'react-redux'
import { COMMON_ERRORS } from '../../../../misc/Constants'
import { toast } from 'react-toastify'
import { getCommunityCategories } from '../../../../actions/programCategoryActions'
import FinancialPerformanceWidget from './financial-performance-widget/FinancialPerformanceWidget'
import ProgramsEnrollmentsActivity from './programs-enrollments-activity-widget/ProgramsEnrollmentsActivityWidget'
import ProgramCategoryPackageBreakdownWidget from './program-category-package-breakdown-widget/ProgramCategoryPackageBreakdownWidget'
import MarketingAnalyticsWidget from './marketing-analytics-widget/MarketingAnalyticsWidget'
import CommunityInformationWidget from './community-information-widget/CommunityInformationWidget'
import MemberGrowthWidget from './member-growth-widget/MemberGrowthWidget'
import LifetimeValueLeaderboardWidget from './lifetime-value-leaderboard-widget/LifetimeValueLeaderboardWidget'
import JumpPill from './JumpPill'

import classes from './BusinessDashboard.module.scss'
import { getProgramsFilterOptions } from '../../../../actions/programActions'

const BusinessDashboard = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const currentUser = useSelector(state => state.user.current_user)
  const [communityHasEnrollments, setCommunityHasEnrollments] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [activeIndex, setActiveIndex] = useState(0)

  const communityId = currentUser.communities[0].id

  useEffect(() => {
    Promise.all([
      requestGetEnrollmentsExist(communityId)
        .then(res => setCommunityHasEnrollments(res.data.exists)),
      dispatch(getCommunityCategories())
        .unwrap(),
      dispatch(getProgramsFilterOptions())
        .unwrap(),
    ]).then(() => setIsLoading(false))
      .catch(() => toast.error(t(COMMON_ERRORS[500])))
  }, [communityId, dispatch])

  const containerRef = useRef(null)

  // Widgets are rendered from SECTIONS in order, so the Nth <section> child of the
  // container is SECTIONS[N] — scroll straight to it by index, no ids needed.
  const scrollToSection = (index) => {
    const widgetSections = containerRef.current?.querySelectorAll(':scope > section')
    widgetSections?.[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Keep the JumpPill in sync with the section in view while scrolling. The dashboard
  // scrolls inside its parent (.main), so we listen there and mark active the last
  // <section> whose top has crossed an activation line near the top of the viewport.
  useEffect(() => {
    const scrollContainer = containerRef.current?.parentElement
    if (!scrollContainer) {
      return undefined
    }

    const updateActiveSection = () => {
      const widgetSections = containerRef.current?.querySelectorAll(':scope > section')
      if (!widgetSections?.length) {
        return
      }
      const activationLine = scrollContainer.getBoundingClientRect().top + 100
      let active = 0
      widgetSections.forEach((section, index) => {
        if (section.getBoundingClientRect().top <= activationLine) {
          active = index
        }
      })
      setActiveIndex(active)
    }

    let ticking = false
    const onScroll = () => {
      if (ticking) {
        return
      }
      ticking = true
      requestAnimationFrame(() => {
        updateActiveSection()
        ticking = false
      })
    }

    updateActiveSection()
    scrollContainer.addEventListener('scroll', onScroll, { passive: true })
    return () => scrollContainer.removeEventListener('scroll', onScroll)
  }, [])

  // Single source of truth for the dashboard widgets: order, JumpPill nav (id + label)
  // and what gets rendered all live together here, so they can't drift apart. The
  // widgets are rendered by mapping this array, and scroll-anchor ids are assigned to
  // the resulting <section>s by the same order.
  const SECTIONS = [
    {
      id: 'widget-financial-performance',
      label: t('dashboard.business.financialPerformance.financialPerformanceWidget.title'),
      render: ({ communityHasEnrollments, isLoading }) => (
        <FinancialPerformanceWidget communityHasEnrollments={communityHasEnrollments} isLoadingEnrollmentsPresence={isLoading} />
      ),
    },
    {
      id: 'widget-programs-enrollments',
      label: t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.title'),
      render: () => <ProgramsEnrollmentsActivity />,
    },
    {
      id: 'widget-category-package-breakdown',
      label: t('dashboard.business.programCategoryPackageBreakdown.programCategoryPackageBreakdownWidget.title'),
      render: () => <ProgramCategoryPackageBreakdownWidget />,
    },
    {
      id: 'widget-marketing-analytics',
      label: t('dashboard.business.marketingAnalytics.marketingAnalyticsWidget.title'),
      render: () => <MarketingAnalyticsWidget />,
    },
    {
      id: 'widget-community-information',
      label: t('dashboard.business.communityInformation.communityInformationWidget.title'),
      render: () => <CommunityInformationWidget />,
    },
    {
      id: 'widget-member-growth',
      label: t('dashboard.business.memberGrowth.memberGrowthWidget.title'),
      render: () => <MemberGrowthWidget />,
    },
    {
      id: 'widget-lifetime-value-leaderboard',
      label: t('dashboard.business.lifetimeValueLeaderboard.lifetimeValueLeaderboardWidget.title'),
      render: () => <LifetimeValueLeaderboardWidget />,
    },
  ]

  return (
    <div ref={containerRef} className={classnames(classes.businessDashboardContainer, 'd-flex flex-column align-items-center')}>
      {SECTIONS.map(section => (
        <Fragment key={section.id}>
          {section.render({ communityHasEnrollments, isLoading })}
        </Fragment>
      ))}
      <div className={classes.spacer} />
      <JumpPill sections={SECTIONS} activeIndex={activeIndex} onSelect={scrollToSection} />
    </div>
  )
}

export default BusinessDashboard
